// src/services/reportSuggestions.js
//
// "Your patients from earlier reports" on the weekly report form. Only
// patients THIS coordinator reported before, for THIS study:
//   - last reported as Screening (no result yet)       -> Screen fail / Enrolled/Randomized
//   - last reported as Enrolled/Randomized or Follow-up -> Follow-up
// Patients already in this week's list for the study aren't suggested again.
//
// Pure and Firebase-free so it is unit-tested (reportSuggestions.test.js).

import { entryIdentity } from "./weeklyReportModel";

const NEXT_STEPS = {
  screening: ["screenFail", "enrolled"],
  enrolled: ["followUp"],
  followUp: ["followUp"],
};

/**
 * priorDocs: this coordinator's saved patient lists (weekly_report_patients),
 *   each { periodStart, createdAtMs, studies: [{ studyDocId, patients: [entry] }] }.
 * Returns [{ entry, lastStatus, lastPeriodStart, nextStatuses }], most recent first.
 */
export const suggestionsFor = ({ priorDocs = [], studyDocId, currentPatients = [] }) => {
  const inList = new Set(currentPatients.map(entryIdentity).filter(Boolean));
  const ordered = [...priorDocs].sort(
    (a, b) =>
      String(a.periodStart || "").localeCompare(String(b.periodStart || "")) ||
      (a.createdAtMs || 0) - (b.createdAtMs || 0)
  );
  const latest = new Map();
  ordered.forEach((doc) => {
    (doc.studies || [])
      .filter((s) => s.studyDocId === studyDocId)
      .forEach((s) =>
        (s.patients || []).forEach((p) => {
          const id = entryIdentity(p);
          if (id) latest.set(id, { entry: p, lastStatus: p.status, lastPeriodStart: doc.periodStart || "" });
        })
      );
  });
  return [...latest.entries()]
    .filter(([id, v]) => !inList.has(id) && NEXT_STEPS[v.lastStatus])
    .map(([, v]) => ({
      entry: {
        patientRef: v.entry.patientRef || null,
        name: v.entry.name || "",
        dob: v.entry.dob || "",
        mrn: v.entry.mrn || "",
        subjectNumber: v.entry.subjectNumber || "",
        notInRegx: !v.entry.patientRef && v.entry.notInRegx === true,
      },
      lastStatus: v.lastStatus,
      lastPeriodStart: v.lastPeriodStart,
      nextStatuses: NEXT_STEPS[v.lastStatus],
    }))
    .sort((a, b) => String(b.lastPeriodStart).localeCompare(String(a.lastPeriodStart)));
};
